import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import AuthShell from "../components/auth/AuthShell";
import { AppAlert, AppButton, AppField } from "../components/ui/AppField";
import { isValidRwandanPhone } from "../utils/paymentHelpers";
import { isValidEmail } from "../utils/profileHelpers";
import {
  clearAppAuthError,
  requestAppPasswordReset,
  selectAppAuthError,
  selectAppAuthLoading,
} from "../redux/appAuthSlice";

const ContactMethodToggle = ({ value, onChange }) => (
  <div className="app-contact-toggle" role="tablist" aria-label="Contact method">
    {[
      { id: "email", label: "Email" },
      { id: "phone", label: "Phone" },
    ].map((option) => (
      <button
        key={option.id}
        type="button"
        role="tab"
        aria-selected={value === option.id}
        className={`app-contact-toggle-btn${
          value === option.id ? " app-contact-toggle-btn-active" : ""
        }`}
        onClick={() => onChange(option.id)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector(selectAppAuthLoading);
  const error = useSelector(selectAppAuthError);

  const [contactMethod, setContactMethod] = useState("email");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [localError, setLocalError] = useState("");
  const [localMessage, setLocalMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLocalError("");
    setLocalMessage("");

    const sanitizedEmail = email.trim().toLowerCase();
    const sanitizedPhone = phoneNumber.trim();
    const sanitizedRecoveryEmail = recoveryEmail.trim().toLowerCase();

    if (contactMethod === "email") {
      if (!isValidEmail(sanitizedEmail)) {
        setLocalError("Enter a valid email address.");
        return;
      }
    } else {
      if (!isValidRwandanPhone(sanitizedPhone)) {
        setLocalError("Enter a valid Rwandan phone number.");
        return;
      }
      if (!isValidEmail(sanitizedRecoveryEmail)) {
        setLocalError("Enter a valid email to receive the reset code.");
        return;
      }
    }

    dispatch(clearAppAuthError());

    try {
      const payload =
        contactMethod === "email"
          ? { email: sanitizedEmail }
          : { phoneNumber: sanitizedPhone, recoveryEmail: sanitizedRecoveryEmail };

      await dispatch(requestAppPasswordReset(payload)).unwrap();

      setLocalMessage(
        contactMethod === "phone"
          ? `Check ${sanitizedRecoveryEmail} for your reset code.`
          : "If the email exists and is verified, you will receive a reset code."
      );

      navigate("/app/reset-password", {
        state: {
          contactMethod,
          email: contactMethod === "email" ? sanitizedEmail : "",
          phoneNumber: contactMethod === "phone" ? sanitizedPhone : "",
          recoveryEmail: contactMethod === "phone" ? sanitizedRecoveryEmail : "",
        },
      });
    } catch {
      /* slice sets error state */
    }
  };

  const displayError = localError || error;

  return (
    <AuthShell
      title="Forgot password"
      subtitle={
        contactMethod === "email"
          ? "Enter your account email and we'll send a 6-digit reset code."
          : "Enter your account phone number and an email where we can send the reset code."
      }
      footer={
        <Link
          to="/app/login"
          className="app-btn-ghost flex h-12 w-full items-center justify-center rounded-lg text-[14px] font-extrabold tracking-tight text-white"
        >
          Back to log in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {displayError ? <AppAlert>{displayError}</AppAlert> : null}
        {localMessage ? (
          <p className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200">
            {localMessage}
          </p>
        ) : null}

        <ContactMethodToggle
          value={contactMethod}
          onChange={(next) => {
            setContactMethod(next);
            setLocalError("");
            dispatch(clearAppAuthError());
          }}
        />

        {contactMethod === "email" ? (
          <AppField
            id="forgot-email"
            label="Account email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        ) : (
          <>
            <AppField
              id="forgot-phone"
              label="Account phone number"
              type="tel"
              inputMode="tel"
              placeholder="0781234567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
            <AppField
              id="forgot-recovery-email"
              label="Email to receive reset code"
              type="email"
              autoComplete="email"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
            />
          </>
        )}

        <AppButton type="submit" disabled={loading}>
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Sending code
            </span>
          ) : (
            "Send reset code"
          )}
        </AppButton>
      </form>
    </AuthShell>
  );
};

export default ForgotPassword;
